<template>
  <v-app>
    <v-container fluid>
      <v-row align="start" justify="center" class="pt-12">
        <v-col cols="12" sm="8" md="5" lg="4">
          <v-card elevation="8" class="pa-4">

            <!-- Verificando token -->
            <div v-if="verifying" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" size="40" />
              <p class="mt-4 grey--text">Verificando enlace...</p>
            </div>

            <!-- Token inválido -->
            <div v-else-if="tokenInvalid" class="text-center">
              <v-icon color="error" size="48" class="mb-3">mdi-alert-circle</v-icon>
              <h2 class="headline mb-2">Enlace inválido</h2>
              <p class="body-1 grey--text text--darken-1 mb-4">
                {{ invalidMessage }}<br />
                Solicita al administrador que te envíe uno nuevo.
              </p>
              <p class="caption grey--text">Redirigiendo al inicio de sesión en {{ countdown }} segundos...</p>
            </div>

            <!-- Éxito -->
            <div v-else-if="success" class="text-center">
              <v-icon color="success" size="48" class="mb-3">mdi-check-circle</v-icon>
              <h2 class="headline mb-2">Contraseña actualizada</h2>
              <p class="body-1 grey--text text--darken-1 mb-6">
                Tu contraseña fue cambiada correctamente.<br />
                Ya puedes iniciar sesión con tu nueva contraseña.
              </p>
              <v-btn color="primary" block @click="goToLogin">
                Ir al inicio de sesión
              </v-btn>
            </div>

            <!-- Formulario -->
            <div v-else>
              <h2 class="headline text-center mb-2">Nueva contraseña</h2>
              <p class="body-2 text-center grey--text mb-2">
                Ingresa tu nueva contraseña.
              </p>

              <v-alert type="info" dense text class="mb-4 body-2">
                <strong>La contraseña debe cumplir:</strong>
                <ul class="mt-1 pl-4" style="list-style: disc;">
                  <li>Mínimo 8 caracteres</li>
                  <li>Al menos una letra mayúscula</li>
                  <li>Al menos una letra minúscula</li>
                  <li>Al menos un número</li>
                  <li>Al menos un carácter especial (!@#$%&*...)</li>
                </ul>
              </v-alert>

              <v-alert v-if="error" type="error" dense outlined dismissible class="mb-4" @input="error = ''">
                {{ error }}
              </v-alert>

              <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit" :disabled="loading">
                <v-text-field
                  v-model="newPassword"
                  label="Nueva contraseña"
                  :type="showNew ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock-plus"
                  :append-icon="showNew ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showNew = !showNew"
                  outlined
                  :rules="[rules.required, rules.minLength, rules.uppercase, rules.lowercase, rules.number, rules.special]"
                  class="mb-1"
                />

                <v-text-field
                  v-model="confirmPassword"
                  label="Confirmar nueva contraseña"
                  :type="showConfirm ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock-check"
                  :append-icon="showConfirm ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showConfirm = !showConfirm"
                  outlined
                  :rules="[rules.required, rules.match]"
                  class="mb-4"
                />

                <v-btn
                  type="submit"
                  color="primary"
                  block
                  large
                  :loading="loading"
                  :disabled="!valid"
                >
                  Cambiar contraseña
                </v-btn>
              </v-form>
            </div>

          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import axios from "axios";

export default {
  name: "ResetPassword",
  data() {
    return {
      verifying: true,
      tokenInvalid: false,
      invalidMessage: "Este enlace no es válido o ya fue utilizado.",
      success: false,
      loading: false,
      error: "",
      valid: false,
      uuid: "",
      token: "",
      countdown: 5,
      newPassword: "",
      confirmPassword: "",
      showNew: false,
      showConfirm: false,
      rules: {
        required: (v) => !!v || "Campo requerido",
        minLength: (v) => (v && v.length >= 8) || "Mínimo 8 caracteres",
        uppercase: (v) => /[A-Z]/.test(v) || "Debe incluir al menos una mayúscula",
        lowercase: (v) => /[a-z]/.test(v) || "Debe incluir al menos una minúscula",
        number: (v) => /[0-9]/.test(v) || "Debe incluir al menos un número",
        special: (v) => /[^A-Za-z0-9]/.test(v) || "Debe incluir al menos un carácter especial",
        match: (v) => v === this.newPassword || "Las contraseñas no coinciden",
      },
    };
  },
  watch: {
    newPassword() {
      if (this.$refs.form) {
        this.$refs.form.validate();
      }
    },
    confirmPassword() {
      if (this.$refs.form) {
        this.$refs.form.validate();
      }
    },
    tokenInvalid(val) {
      if (val) {
        this.startCountdown();
      }
    },
  },
  created() {
    this.uuid = this.$route.query.uuid || "";
    this.token = this.$route.query.token || "";

    if (!this.uuid || !this.token) {
      this.tokenInvalid = true;
      this.verifying = false;
      return;
    }

    this.verifyToken();
  },
  methods: {
    goToLogin() {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      this.$router.push("/");
    },
    startCountdown() {
      const interval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(interval);
          this.goToLogin();
        }
      }, 1000);
    },
    async verifyToken() {      const baseUrl = process.env.VUE_APP_BASE_URL;
      try {
        await axios.get(`${baseUrl}/verify-reset-token`, {
          params: { uuid: this.uuid, token: this.token },
        });
      } catch (e) {
        this.tokenInvalid = true;
        if (e.response && e.response.data && e.response.data.message) {
          this.invalidMessage = e.response.data.message;
        }
      } finally {
        this.verifying = false;
      }
    },

    async handleSubmit() {      if (!this.$refs.form.validate()) return;

      this.loading = true;
      this.error = "";
      const baseUrl = process.env.VUE_APP_BASE_URL;

      try {
        await axios.post(`${baseUrl}/reset-password-token`, {
          uuid: this.uuid,
          token: this.token,
          new_password: this.newPassword,
          new_password_confirmation: this.confirmPassword,
        });
        this.success = true;
      } catch (e) {
        if (e.response && e.response.status === 422) {
          const data = e.response.data;
          if (data.errors) {
            this.error = Object.values(data.errors).flat().join(" ");
          } else {
            this.error = data.message || "Error al cambiar la contraseña.";
          }
          if (this.error.includes("válido") || this.error.includes("utilizado")) {
            this.tokenInvalid = true;
            this.invalidMessage = this.error;
          }
        } else {
          this.error = "Ocurrió un error inesperado. Intenta de nuevo.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
